import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "../components/Spinner";
import PositiveSSLSeal from "../components/PositiveSSLSeal";
import StarekContext from "../context/StarekContext";
import SignInErrorAlert from "../components/SignInErrorAlert";
import Recaptcha from "../components/Recaptcha";
import { describeSignInError } from "../utils/signInErrors";

// Unset in local/dev environments — the captcha gate is then skipped entirely
// so the sign-in form keeps working without keys.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function Login(props) {
    const navigator = useNavigate();
    const starekContext = useContext(StarekContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showSpinner, setShowSpinner] = useState(false);

    // Credentials may be pre-supplied via query string (used by the public
    // demo page to open a read-only account without typing them out):
    //   /login?workspace=acme&username=user@x.com&password=secret
    // The fields are filled in only — the visitor still presses Sign In, so a
    // crafted URL can never drive an unattended sign-in attempt.
    const prefilled = {
        workspace: searchParams.get("workspace") || null,
        username: searchParams.get("username") || null,
        password: searchParams.get("password") || null,
    };

    const [workspace, setWorkspace] = useState(
        prefilled.workspace || starekContext?.lastUsedWorkspace?.workspace_id
    );
    const [termsAccepted, setTermsAccepted] = useState(
        localStorage.getItem("termsAccepted") === "true"
    );
    const [username, setUsername] = useState(prefilled.username);
    const [password, setPassword] = useState(prefilled.password);
    // Last failed attempt, described for display (see signInErrors.js).
    // Cleared when a new attempt starts.
    const [signInError, setSignInError] = useState(null);
    const invalidClass = (field) =>
        signInError?.field === field ? " is-invalid" : "";

    // reCAPTCHA v2 checkbox gate on password sign-in. Tokens are single-use,
    // so the widget is reset after every attempt that consumed one.
    const captchaRequired = Boolean(RECAPTCHA_SITE_KEY);
    const captchaRef = useRef(null);
    const [captchaToken, setCaptchaToken] = useState(null);
    const captchaSolved = !captchaRequired || Boolean(captchaToken);

    const performSignIn = async (ws, user, pass) => {
        if (!captchaSolved) return;
        setSignInError(null);
        try {
            setShowSpinner(true);
            const auth = await starekContext?.login(ws, user, pass);
            setShowSpinner(false);

            if (auth?.user?.must_change_password) {
                navigator(
                    `/reset-password?workspace=${encodeURIComponent(ws)}&forced=true`,
                );
                return;
            }

            const redirectPath =
                sessionStorage.getItem("postAuthRedirect") || "/";
            navigator(redirectPath);
        } catch (e) {
            console.error("[Login] sign-in failed:", e);
            setSignInError(describeSignInError(e));
            setShowSpinner(false);
        } finally {
            captchaRef.current?.reset();
        }
    };

    const signIn = () => performSignIn(workspace, username, password);

    useEffect(() => {
        localStorage.setItem("termsAccepted", termsAccepted);
    }, [termsAccepted]);

    useEffect(() => {
        // Fields hold the values now, so drop them from the address bar and
        // history straight away instead of leaving them on screen.
        if (prefilled.workspace || prefilled.username || prefilled.password) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="login-page">
            <div className="login-box">
                <div
                    className="login-logo d-flex flex-column align-items-center justify-content-center"
                    style={{ marginBottom: "1rem" }}
                >
                    <img
                        src="/dist/img/light-starek-brand-512x512.png"
                        alt="Starek"
                        className="brand-image elevation-3"
                        style={{ opacity: "0.8" }}
                    />
                </div>

                {/* Workspace field at the top */}
                <div className="mb-4">
                    <div className="input-group has-validation">
                        <input
                            type="text"
                            className={`form-control${invalidClass("workspace")}`}
                            placeholder="Workspace"
                            onChange={(e) => setWorkspace(e.target.value)}
                            value={workspace}
                        />
                        <div className="input-group-append">
                            <div className="input-group-text">
                                <span className="fas fa-globe" />
                            </div>
                        </div>
                        <div className="invalid-feedback">
                            {signInError?.field === "workspace"
                                ? null
                                : "Please provide your workspace"}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body login-card-body">
                        <>
                            <SignInErrorAlert
                                error={signInError}
                                onRetry={signIn}
                            />
                            <div className="input-group has-validation mb-3">
                                <input
                                    type="email"
                                    className={`form-control${invalidClass("username")}`}
                                    placeholder="Email"
                                    onChange={(e) =>
                                        setUsername(e.target.value)
                                    }
                                    value={username || ""}
                                />
                                <div className="input-group-append">
                                    <div className="input-group-text">
                                        <span className="fas fa-envelope" />
                                    </div>
                                </div>
                                <div className="invalid-feedback">
                                    {signInError?.field === "username"
                                        ? null
                                        : "Please provide your username"}
                                </div>
                            </div>
                            <div className="input-group has-validation mb-3">
                                <input
                                    type="password"
                                    className={`form-control${invalidClass("password")}`}
                                    placeholder="Password"
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    value={password || ""}
                                />
                                <div className="input-group-append">
                                    <div className="input-group-text">
                                        <span className="fas fa-lock" />
                                    </div>
                                </div>
                                <div className="invalid-feedback">
                                    {signInError?.field === "password"
                                        ? null
                                        : "Please provide your password"}
                                </div>
                            </div>
                            <Recaptcha
                                ref={captchaRef}
                                siteKey={RECAPTCHA_SITE_KEY}
                                onChange={setCaptchaToken}
                            />
                            <div className="row">
                                <div className="col-8">
                                    <p className="mb-1">
                                        <Link to="/forgot-password">
                                            Trouble Signing-in?
                                        </Link>
                                    </p>
                                </div>
                                <div className="col-4">
                                    <button
                                        className="btn btn-primary btn-block"
                                        disabled={!termsAccepted || !captchaSolved}
                                        onClick={signIn}
                                        title={
                                            !captchaSolved
                                                ? "Confirm you are not a robot first"
                                                : undefined
                                        }
                                    >
                                        Sign In
                                    </button>
                                </div>
                            </div>
                            {/* Horizontal line between Sign In and SSO buttons */}
                            <hr className="my-4" />
                            {showSpinner ? (
                                <Spinner />
                            ) : (
                                <>
                                    <div className="mt-3">
                                        <button
                                            className="btn btn-block mb-2"
                                            style={{
                                                backgroundColor: "#fff",
                                                color: "#222",
                                                border: "1px solid #ddd",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 500,
                                                fontSize: "16px",
                                                gap: "8px",
                                            }}
                                            onClick={async () => {
                                                try {
                                                    const returnUriPath =
                                                        sessionStorage.getItem(
                                                            "postAuthRedirect"
                                                        ) || "/";

                                                    const query =
                                                        new URLSearchParams({
                                                            client_id:
                                                                process.env
                                                                    .REACT_APP_STAREK_AUTH_CLIENT_ID,
                                                            response_type:
                                                                "code",
                                                            scope: "email openid",
                                                            redirect_uri: `${process.env.REACT_APP_HOST}/sso-authenticate`,
                                                            state: JSON.stringify(
                                                                {
                                                                    workspace,
                                                                    returnUriHost: `${process.env.REACT_APP_HOST}`,
                                                                    returnUriPath,
                                                                }
                                                            ),
                                                        });
                                                    const uri = `${
                                                        process.env
                                                            .REACT_APP_STAREK_AUTH_URI
                                                    }/login?${query.toString()}`;
                                                    window.location.href = uri;
                                                } catch (e) {
                                                    console.error(
                                                        "[Login] could not start SSO:",
                                                        e,
                                                    );
                                                    setSignInError(
                                                        describeSignInError(e),
                                                    );
                                                    setShowSpinner(false);
                                                }
                                            }}
                                            disabled={!workspace}
                                        >
                                            <img
                                                src="https://00117-starek.s3.us-east-1.amazonaws.com/branding/dark-starek-logo-80x80.png"
                                                alt="Starek"
                                                style={{
                                                    width: "24px",
                                                    height: "24px",
                                                    borderRadius: "50%",
                                                }}
                                            />
                                            Sign in with Starek
                                        </button>
                                        {/* <button
                                            className="btn btn-block mb-2"
                                            style={{
                                                backgroundColor: "#fff",
                                                color: "#444",
                                                border: "1px solid #ddd",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 500,
                                                fontSize: "16px",
                                                gap: "8px",
                                            }}
                                            onClick={() =>
                                                (window.location.href =
                                                    "/sso-authenticate-google")
                                            }
                                            disabled={!workspace}
                                        >
                                            <img
                                                src="https://developers.google.com/identity/images/g-logo.png"
                                                alt="Google"
                                                style={{
                                                    width: "20px",
                                                    height: "20px",
                                                }}
                                            />
                                            Sign in with Google
                                        </button> */}
                                        <div className="form-check mt-3">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="termsCheckbox"
                                                checked={termsAccepted}
                                                onChange={(e) =>
                                                    setTermsAccepted(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="termsCheckbox"
                                            >
                                                By signing in, you accept our{" "}
                                                <a href="/onboard">
                                                    terms & conditions
                                                </a>
                                                .
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    </div>
                </div>

                <div className="text-white" style={{ textAlign: "center" }}>
                    <br />
                    Structured Telemetry and Reporting
                </div>
                {/* PositiveSSL Trust Seal */}
                <PositiveSSLSeal />
            </div>
        </div>
    );
}

export default Login;
